import { DiagnosticReferentialData } from "@/modules/dwelling/reducers";
import { NotificationRecipientsForm } from "@/modules/order/components/form/OrderNotificationRecipientsFormModal.tsx";
import { ServiceReportFile } from "@/modules/serviceOrdering/interfaces/ServiceReportFile.ts";

import Axios from "../../../_helpers/axiosHelper";
import {
  IService,
  NotificationRecipient,
} from "../../serviceOrdering/reducers";
import { TenantForm } from "../components/form/TenantFormModal.tsx";

export const getService = (id: string): Promise<IService> => {
  return Axios.get(`/agency/service/${id}`);
};

export const updateService = (id: string, data: any) => {
  return Axios.put(`/service`, data);
};

export const updateServiceExternalUrl = (
  id: string,
  externalUrls: Array<{
    id?: string;
    name: string;
    url: string;
    type: string;
  }>
): Promise<any> => {
  return Axios.put(`/agency/service/${id}/external-url`, {
    externalUrls,
  });
};

export const updateServiceOrderedBy = (id: string, userId: string) => {
  return Axios.put(`/agency/service/${id}/ordered-by/${userId}`);
};

export const updateStatus = (serviceId: string, slug: string): Promise<any> => {
  return Axios.put(`/agency/service-status/${serviceId}`, { slug });
};

export const updateServiceOperator = (
  serviceId: string,
  operatorId: string
): Promise<any> => {
  return Axios.put(`/agency/service-operator/${serviceId}`, {
    user: { id: operatorId },
  });
};

export const updateServiceAppointment = (
  serviceId: string,
  dates: { startDate: string; endDate?: string }
): Promise<any> => {
  return Axios.put(`/agency/service-appointment/${serviceId}`, dates);
};

export const createServiceComment = (
  serviceId: string,
  comment: string,
  isExternal: boolean
): Promise<any> => {
  return Axios.put(`/agency/service-comment/${serviceId}`, {
    comment,
    isExternal,
  });
};

export const updateServiceComment = (
  serviceId: string,
  commentId: string,
  comment: string,
  isExternal: boolean
): Promise<any> => {
  return Axios.put(`/agency/service-comment/${serviceId}/update/${commentId}`, {
    comment,
    isExternal,
  });
};

export const deleteServiceComment = (
  serviceId: string,
  commentId: string
): Promise<any> => {
  return Axios.delete(
    `/agency/service-comment/${serviceId}/delete/${commentId}`
  );
};

export const updateServiceTenant = (
  serviceId: string,
  tenantId: string,
  tenant: TenantForm
): Promise<any> => {
  return Axios.put(`/agency/service-tenant/${serviceId}`, {
    id: tenantId,
    ...tenant,
  });
};

export const createServiceTenant = (
  serviceId: string,
  tenant: TenantForm
): Promise<any> => {
  return Axios.post(`/agency/service-tenant/${serviceId}`, {
    ...tenant,
  });
};

export const uploadFilesService = (
  serviceReportId: string,
  files: any[]
): Promise<any> => {
  const formData = new FormData();
  files.forEach((file, index) => {
    formData.append(`files[${index}][content]`, file.content);
    if (file.url) {
      formData.append(`files[${index}][exist]`, "true");
    }
    formData.append(`files[${index}][type][label]`, file.type.label);
    formData.append(`files[${index}][type][value]`, file.type.value);
  });

  return Axios.post(`/service-report/${serviceReportId}/files`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteFileServiceReport = (
  serviceReportId: string,
  fileId: string
): Promise<any> => {
  return Axios.delete(`/service-report/${serviceReportId}/file/${fileId}`);
};

export const regenerateServiceReportPDF = (
  serviceReportId: string
): Promise<any> => {
  return Axios.get(`/admin/regenerate-pdf/${serviceReportId}`);
};

export const updateServiceMainFile = (
  serviceId: string,
  fileId: string
): Promise<any> => {
  return Axios.put(`/agency/service-main-file/${serviceId}/`, {
    file: { id: fileId },
  });
};

export const resendEmailReport = (serviceId: string): Promise<any> => {
  return Axios.get(`/agency/service-resend-email-report/${serviceId}/`);
};

export const exportServices = (): Promise<any> => {
  return Axios.get(`/agencies/services-export`);
};

export const updateDiagnostic = (
  id: string,
  diagnosticReferentialData: DiagnosticReferentialData
): Promise<DiagnosticReferentialData> => {
  return Axios.post(
    `service/${id}/service-report/diagnostic`,
    diagnosticReferentialData
  );
};

export const getNotificationRecipients = (
  id: string
): Promise<NotificationRecipient> => {
  return Axios.get(`/agency/service-notification-recipient/${id}`);
};

export const updateNotificationRecipients = (
  id: string,
  notificationRecipientsData: NotificationRecipientsForm
): Promise<NotificationRecipientsForm> => {
  return Axios.put(
    `/agency/service-notification-recipient/${id}`,
    notificationRecipientsData
  );
};

export const updateManyServiceReportFiles = (
  serviceReportFiles: ServiceReportFile[]
): Promise<ServiceReportFile[]> => {
  return Axios.patch(`/serviceReportFile/updateMany`, serviceReportFiles);
};
